import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { addConditionalSplitSends } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignStatus } from 'models/enums';
import useQueryParams from 'hooks/useQueryParams';
import { getSteps, MAX_SENDS_AMOUNT, renderChooseStepsForm } from '../utils';
import { SplitTypes } from 'domains/campaigns/types';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseSendTypeStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    const isEditingPausedCampaign = () => hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;

    const getIsStepDisabled = (step: CustomCampaignSteps) => {
        // For paused curations: enable only the option that matches the current send type
        if (isEditingPausedCampaign()) {
            const currentSend = campaignState.sends[activeSendIndex] as any;
            const isConditionalSplit = !!currentSend?.splitAction && !!currentSend?.splitType;
            const isIndependentEmail = !isConditionalSplit;

            switch (step) {
                case CustomCampaignSteps.SELECT_CONTENT:
                    // Email is enabled only if current send is an independent email (no splitAction/splitType)
                    return !isIndependentEmail;
                case CustomCampaignSteps.SET_TIME_DELAY:
                    return true;
                case CustomCampaignSteps.CONDITIONAL_SPLIT:
                    // Conditional Split is enabled only if current send has splitAction/splitType
                    return !isConditionalSplit;
                case CustomCampaignSteps.SCHEDULE_CURATION:
                    return false;
                default:
                    return false;
            }
        }

        // Default logic for non-paused curations
        switch (step) {
            case CustomCampaignSteps.SELECT_CONTENT:
                return false;
            case CustomCampaignSteps.SET_TIME_DELAY:
                return true;
            case CustomCampaignSteps.CONDITIONAL_SPLIT:
                return false;
            case CustomCampaignSteps.SCHEDULE_CURATION:
                return true;
            default:
                return false;
        }
    };

    useEffect(() => {
        const isSelectedStepDisabled = getIsStepDisabled(selectedStep);
        if (isSelectedStepDisabled) return;

        if (selectedStep) setActiveStep(selectedStep);

        // We only want this to run when the send index or selected step change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, selectedStep]);

    // Handles adding/configuring sends based on next step selection
    useEffect(() => {
        // Configure send added after time delay as conditional split
        // and add "does not meet criteria" send
        if (selectedStep === CustomCampaignSteps.CONDITIONAL_SPLIT) {
            const nextSendIndex = activeSendIndex + 1;
            const nextSend = campaignState.sends[nextSendIndex];
            const doesNotMeetSendExists =
                nextSend && 'splitType' in nextSend && nextSend.splitType === SplitTypes.DOES_NOT_MEET;

            if (!doesNotMeetSendExists) {
                dispatchCampaignAction(addConditionalSplitSends(activeSendIndex));
            }
        }

        // We only want this to run when the send index or selected step change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, selectedStep]);

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedStep((event.target as HTMLInputElement).value);
    };

    const maxSendsReached = campaignState.sends.length === MAX_SENDS_AMOUNT;
    const steps = getSteps(getIsStepDisabled, maxSendsReached, isEditingPausedCampaign());

    return renderChooseStepsForm(handleStepClick, selectedStep, steps);
};

export default CustomCampaignChooseSendTypeStep;
